import React, { useState, useEffect } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Droplets } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function HydrationGauge() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState({ total_ml: 0, target_ml: 3000, percentage: 0, needs_electrolyte_warning: false, fasting_active: false });

  useEffect(() => {
    api.get('/hydration/today')
      .then(res => setData({
        total_ml: res.data.total_ml || 0,
        target_ml: res.data.target_ml || 3000,
        percentage: res.data.percentage || 0,
        needs_electrolyte_warning: res.data.needs_electrolyte_warning || false,
        fasting_active: res.data.fasting_active || false,
      }))
      .catch(() => {});
  }, []);

  const glasses = Math.round(data.total_ml / 250);
  const targetGlasses = Math.round(data.target_ml / 250);
  const pct = Math.min(data.percentage, 100);
  const isComplete = pct >= 100;

  // Yuri spec 2026-05-06: Hydration gets its own "Blue Electric Glow"
  // signature (mirror of the orange-fluo for pas/protéines). Always blue,
  // always glowing — just a notch brighter + pulse when 100%. That way the
  // water identity is preserved while still delivering a dopamine signal.
  const BLUE_GLOW = '0 0 14px rgba(56,189,248,0.55), 0 0 4px rgba(59,130,246,0.45)';
  const BLUE_GLOW_STRONG = '0 0 22px rgba(56,189,248,0.75), 0 0 8px rgba(59,130,246,0.65)';
  const cardBorder = 'rgba(56,189,248,0.45)';
  const cardBg = 'linear-gradient(135deg, rgba(56,189,248,0.12), rgba(59,130,246,0.05))';
  const accent = '#38bdf8';       // sky-400
  const ringBg = 'rgba(56,189,248,0.18)';
  const valueColor = 'text-sky-300';
  const dropletColor = 'text-sky-300';
  const badgePalette = 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/40';

  return (
    <div
      onClick={() => navigate('/hydration')}
      className="relative overflow-hidden rounded-xl border cursor-pointer transition-all group"
      style={{
        borderColor: cardBorder,
        background: cardBg,
        boxShadow: isComplete ? BLUE_GLOW_STRONG : BLUE_GLOW,
      }}
      data-testid="hydration-gauge"
    >
      <div className="p-4 flex items-center gap-4">
        {/* Water icon with ring — stronger stroke + glow at 100% */}
        <div className="relative w-14 h-14 shrink-0" style={{ filter: `drop-shadow(${isComplete ? BLUE_GLOW_STRONG : BLUE_GLOW})` }}>
          <svg viewBox="0 0 48 48" className="w-full h-full">
            <circle cx="24" cy="24" r="20" fill="none" stroke={ringBg} strokeWidth="4" />
            <circle
              cx="24" cy="24" r="20"
              fill="none"
              stroke={accent}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${pct * 1.256} 125.6`}
              transform="rotate(-90 24 24)"
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Droplets className={`w-5 h-5 ${dropletColor}`} />
          </div>
        </div>

        {/* Stats */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className={`text-xl font-black ${valueColor}`}>
              {glasses}
            </span>
            <span className="text-sm text-muted-foreground">/ {targetGlasses}</span>
            <span className="text-xs text-muted-foreground/60">
              {language === 'fr' ? 'verres' : 'glasses'}
            </span>
          </div>
          {/* Progress bar — Blue Electric Glow signature, brighter at 100% */}
          <div className="mt-1.5 h-1.5 bg-secondary/50 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-300"
              style={{
                width: `${pct}%`,
                boxShadow: isComplete ? BLUE_GLOW_STRONG : BLUE_GLOW,
              }}
            />
          </div>
          <p className={`text-[11px] mt-1 ${isComplete ? 'text-sky-300 font-bold uppercase tracking-widest' : 'text-muted-foreground'}`}>
            {isComplete
              ? (language === 'fr' ? '✓ Cible atteinte' : '✓ Goal hit')
              : `${(data.total_ml / 1000).toFixed(1)}L / ${(data.target_ml / 1000).toFixed(1)}L`}
          </p>
        </div>

        {/* Percentage badge */}
        <div className={`text-xs font-bold px-2 py-1 rounded-full ${badgePalette}`}>
          {Math.round(pct)}%
        </div>
      </div>
      
      {/* Electrolyte Coach Tip — shown if target > 3L or fasting active */}
      {data.needs_electrolyte_warning && (
        <div className="px-4 pb-3 -mt-1" data-testid="electrolyte-warning">
          <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <span className="text-sm shrink-0 mt-0.5">💡</span>
            <p className="text-[10px] leading-tight text-amber-300/90">
              {language === 'fr'
                ? `Conseil du Coach : ${data.fasting_active ? 'Tu es en jeune actif. ' : ''}Assure-toi d'ajouter une pincee de sel marin ou des electrolytes pour ne pas demineraliser ton corps${data.fasting_active ? ', surtout pendant ton jeune' : ''} !`
                : `Coach Tip: ${data.fasting_active ? 'You\'re actively fasting. ' : ''}Make sure to add a pinch of sea salt or electrolytes to avoid demineralizing your body${data.fasting_active ? ', especially during your fast' : ''}!`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
